import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  NavigateBefore,
  NavigateNext,
} from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { LoadingButton } from '@mui/lab';
import { Button, Chip, Divider, IconButton, Menu, MenuItem, SvgIcon, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { resetCoSignatureStatus, updateCoSignatureStatus } from 'src/redux/actions/therapist-dashboard-action';
import {
  getTherapistDocumentCreateEditDataAction,
  resetTherapistDocumentCreateEditDataAction,
} from 'src/redux/actions/documentationAction';
import { getAccessToken } from 'src/routes/AuthContext';
import { canDo, SignatureAction } from 'src/types/signatureContext';
import useClockInOutButtonVisibility from 'src/hooks/use-clock-in-out-button-visibility';
import { useStopwatch } from 'src/layouts/check-in-checkout/StopwatchContext';
import useDialogVisibility from 'src/utils/dialog-hide';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import ClockInOutModal from 'src/pages/therapist-dashboard/time-in-out/clock-in-out-modal';

const DocumentPreviewCarousel = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch()
  const [documents, setDocuments] = useState<any[]>(location?.state?.document || []);
  const [currentIndex, setCurrentIndex] = useState(location?.state?.index || 0);
  const [selectedDoc, setSelectedDoc] = useState<any>([])
  const [iframeRef, setIframeRef] = useState<any | null>(null);
  const coSignatureReview = useSelector((state: any) => state.coSignatureReviewReducer)
  const [isWarning, setIsWarning] = useState(false);
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [message, setMessage] = useState('');
  // Patient selection states
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // APIT-587: gate the supervisor Edit action with the per-facility clock-in flag.
  const { refetch: refetchClockInOutVisibility } = useClockInOutButtonVisibility();
  const { canShowDialog: canShowClockInDialog } = useDialogVisibility('clock_in_do_not_show');
  const { isRunning, startStopwatch } = useStopwatch();
  const checkoutResponse = useSelector((state: any) => state.checkoutReducer);
  const [isClockIn, setIsClockIn] = useState(false);
  const [notToShowAgain, setNotToShowAgain] = useState(false);
  // The document to resume editing after the clock-in popup is resolved.
  const [docForEdit, setDocForEdit] = useState<any>(null);

  useEffect(() => {
    if (documents.length > 0) {
      setSelectedDoc(documents[currentIndex]);
    }
  }, [currentIndex, documents]);

  const updateIFrameRef = useCallback((node: any) => {
    setIframeRef(node);
  }, []);

  useEffect(() => {
    const iframeElement = iframeRef;

    if (iframeElement) {
      const interval = setInterval(async () => {
        const response = await getAccessToken();
      }, 15000);

      return () => {
        clearInterval(interval);
      };
    }
  }, [iframeRef]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev: any) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < documents.length - 1) {
      setCurrentIndex((prev: any) => prev + 1);
    }
  };

  const handlePatientClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePatientClose = () => {
    setAnchorEl(null);
  };

  const handlePatientSelect = (document: any) => {
    setSelectedDoc(document);
    handlePatientClose();
  };

  // Perform the actual supervisor edit navigation. Extracted so the clock-in
  // resume paths (handleIsClock / post-clock-in effect) can replay it.
  const proceedDocumentEdit = (document: any) => {
    dispatch(resetTherapistDocumentCreateEditDataAction());
    dispatch(getTherapistDocumentCreateEditDataAction(document));
    navigate('/document/document-edit', {
      state: {
        document,
        isSupervisorEdit: true,
      },
    });
  };

  const handleDocumentEdit = async (document: any) => {
    setDocForEdit(document);
    // clock in modal show — re-fetch the per-facility flag NOW so this operation
    // uses the latest value (no page/tab refresh). Any error/404 -> false (hide).
    const canShowClockInOut = await refetchClockInOutVisibility();
    if (canShowClockInDialog() && !isRunning && !notToShowAgain && canShowClockInOut) {
      setIsClockIn(true);
      return;
    }
    proceedDocumentEdit(document);
  };

  // "No, Don't Clock-in" — proceed to the edit without clocking in.
  const handleIsClock = () => {
    proceedDocumentEdit(docForEdit);
    setNotToShowAgain(true);
    setIsClockIn(false);
  };

  // "Yes, Start the Clock" — begin the stopwatch; the post-clock-in effect resumes the edit.
  const HandleStartClock = () => {
    const millageInfo = {
      locationType: 'BUILDING',
      payByMilage: null,
    };
    startStopwatch('MILLAGE', millageInfo);
  };

  // Resume the edit once the clock-in succeeds.
  useEffect(() => {
    if (
      checkoutResponse?.isGetCheckIn &&
      checkoutResponse?.response !== null &&
      checkoutResponse?.clockAction === 'CLOCK_IN_SUCCESS' &&
      docForEdit
    ) {
      proceedDocumentEdit(docForEdit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResponse]);

  // THIN-7261: REJECTED is gone; this only ever sends APPROVED now. The endpoint
  // still takes a SuperviseStatus param on the wire (kept for backward compat with
  // 3rd-party DB readers of supervise_status) but the FE side has collapsed to a
  // single approval action.
  const approveDocument = (document: any) => {
    dispatch(updateCoSignatureStatus({
      formUuid: document?.uuid,
      superviseStatus: 'APPROVED'
    }))
  }

  useEffect(() => {
    if (
      !coSignatureReview?.isCoSignature &&
      coSignatureReview?.response === null
    )
      return;
    if (
      !coSignatureReview?.isCoSignature &&
      coSignatureReview !== null
    ) {
      setMessage(coSignatureReview?.response?.message);
      setIsSnackBarOpen(true);
      setTimeout(() => {
        setIsSnackBarOpen(false);
        dispatch(resetCoSignatureStatus());
        setMessage('');
      }, 3000);
      return;
    } else {
      // THIN-7261: only the APPROVED path remains; dual-write superviseStatus + the
      // canonical approvedForSupervisorSignature locally so chip + button gating stay
      // consistent with the BE without waiting for a refetch.
      setSelectedDoc((prev: any) => ({
        ...prev,
        superviseStatus: 'APPROVED',
        approvedForSupervisorSignature: true,
      }));

      setDocuments(prev => prev.map(doc =>
        doc.uuid === selectedDoc.uuid
          ? { ...doc, superviseStatus: 'APPROVED', approvedForSupervisorSignature: true }
          : doc
      ));

      setMessage('Document Approved');
      setIsSnackBarOpen(true);
      if (currentIndex < documents.length - 1) {
        setCurrentIndex((prev: any) => prev + 1);
      }
      setTimeout(() => {
        setMessage('');
        setIsSnackBarOpen(false);
        dispatch(resetCoSignatureStatus());
      }, 3000);
    }
  }, [coSignatureReview]);

  return (
    <Stack
      flexDirection={'column'}
      rowGap={2}
      padding={'20px'}
      height={'100%'}
    >
      <Stack sx={{ display: 'flex', textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={'10px'}
          >
            <Typography
              variant="h5"
              noWrap
            >
              Signature Pending
            </Typography>
            <Chip
              label={`${currentIndex + 1}/${documents?.length} Documents`}
              variant="outlined"
              sx={{ marginLeft: '10px', border: '1px solid #afb3ba9e' }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              padding: '6px 16px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button
              id="basic-button"
              onClick={handlePatientClick}
              sx={{
                borderColor: '#afb3ba9e',
                color: '#111927',
                height: '34px',
                display: 'flex',
                justifyContent: 'flex-start'
              }}
              variant="outlined"
              endIcon={anchorEl ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                {selectedDoc?.patientName}
              </Typography>
            </Button>
            <Menu
              id="patient-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handlePatientClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
              PaperProps={{
                sx: {
                  width: 200,
                  '& .MuiMenuItem-root + .MuiDivider-root': {
                    marginTop: '0px',
                  },
                },
              }}
            >
              {documents?.map((documents: any) => (
                <MenuItem
                  key={documents?.uuid}
                  onClick={() => handlePatientSelect(documents)}
                  sx={{
                    overflow: 'hidden',
                    backgroundColor:
                      selectedDoc?.uuid === documents?.uuid ? 'rgb(206 207 255 / 50%)' : 'none',
                  }}
                  selected={selectedDoc?.uuid === documents.uuid}
                >
                  {documents?.patientName}
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Box sx={{ display: 'flex', ml: '-10px' }}>
            {/* THIN-7261: Rejected chip removed; supervise_status no longer carries a
                rejection state. Approval is read off approvedForSupervisorSignature, the
                canonical signal that also drives the bulk-sign queue. */}
            {selectedDoc?.approvedForSupervisorSignature ? (
              <Chip
                label={
                  <Typography variant="overline">
                    Approved
                  </Typography>
                }
                variant="outlined"
                sx={{
                  marginLeft: '10px',
                  color: '#6366F1',
                  backgroundColor: '#ECECFD',
                }}
              />
            ) : (
              <Chip
                label={
                  <Typography variant="overline">
                    pending Review
                  </Typography>
                }
                variant="outlined"
                sx={{
                  marginLeft: '10px',
                  color: '#B54708',
                  backgroundColor: '#F790091F',
                }}
              />
            )}
          </Box>
          <Divider
            orientation="vertical"
            sx={{ m: '10px' }}
          />

          <Box
            gap="1rem"
            alignItems="center"
            flexDirection="row"
            width={'100%'}
            display={'flex'}
          >
            <IconButton
              sx={{
                width: '32px',
                height: '32px',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #F1F1F1',
                background: 'rgba(99, 102, 241, 0.10)',
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.10)',
                },
              }}
              disabled={currentIndex === 0}
              onClick={handlePrevious}
            >
              <NavigateBefore sx={{ color: '#6366F1', fontSize: '14px' }} />
            </IconButton>
            {/* THIN-7261: the supervisor's old Reject button lived here. It was
                retired in favor of the Edit (cosigner-edit) path immediately below
                when SignatureAction.CAN_EDIT became the canonical "revise this doc"
                affordance. With SuperviseStatus.REJECTED no longer accepted by the
                BE, the dead commented-out button is gone too. */}
            {canDo(selectedDoc?.signatureContext?.allowedSupervisorSignatureActions, SignatureAction.CAN_EDIT) && (
              <LoadingButton
                size="large"
                variant='outlined'
                sx={{
                  whiteSpace: 'nowrap',
                  height: 38,
                  width: 120,
                  '.MuiLoadingButton-loadingIndicator': {
                    left: 15,
                  },
                }}
                loadingPosition="start"
                loading={coSignatureReview?.loading}
                onClick={() => handleDocumentEdit(selectedDoc)}
              >
                Edit
              </LoadingButton>
            )}
            <LoadingButton
              size="large"
              sx={{
                whiteSpace: 'nowrap',
                height: 38,
                width: 220,
                '.MuiLoadingButton-loadingIndicator': {
                  left: 15,
                },
              }}
              loadingPosition="start"
              onClick={() => { approveDocument(selectedDoc) }}
              variant="contained"
              loading={coSignatureReview?.loading}
              disabled={selectedDoc?.approvedForSupervisorSignature}
            >
              Approve & Continue
            </LoadingButton>
            <IconButton
              onClick={handleNext}
              disabled={currentIndex === documents.length - 1}
              sx={{
                width: '32px',
                height: '32px',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #F1F1F1',
                background: 'rgba(99, 102, 241, 0.10)',
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.10)',
                },
              }}
            >
              <NavigateNext sx={{ color: '#6366F1', fontSize: '14px' }} />
            </IconButton>
          </Box>
        </Box>
      </Stack>
      <Divider orientation="horizontal" />
      <Stack
        sx={{ display: 'flex', textAlign: 'center' }}
        mb={1}
      >
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          onClick={() => {
            navigate("/dashboard")
          }}
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
        >
          <Typography variant="subtitle1">Back to all docs</Typography>
        </Button>
      </Stack>

      <Box sx={{ height: '100%' }}>
        <iframe
          src={selectedDoc?.pdfUrl}
          ref={updateIFrameRef}
          width={'100%'}
          height={'100%'}
          style={{ border: 'none' }}
          title="Documents"
        />
      </Box>
      {isSnackBarOpen && (
        <SimpleSnackbar
          message={message}
          isWarning={isWarning}

        />
      )}

      {/* clock in modal (APIT-587) */}
      {canShowClockInDialog() && (
        <BootstrapDialog
          PaperProps={{ style: { maxWidth: '750px' } }}
          onClose={() => setIsClockIn(false)}
          aria-labelledby="customized-dialog-title"
          open={isClockIn && !notToShowAgain}
        >
          <ClockInOutModal
            close={handleIsClock}
            handleClockIn={HandleStartClock}
          />
        </BootstrapDialog>
      )}
    </Stack>
  );
};

export default DocumentPreviewCarousel;
